// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React from 'react';
import './SplitButton.css';

export interface SplitButtonOption<T extends string = string> {
  /** The value reported to `onChange` when this segment is selected. */
  value: T;
  /** Segment label (text or node). */
  label: React.ReactNode;
  /** Disable just this segment. */
  disabled?: boolean;
  /** Optional per-segment test id. */
  'data-testid'?: string;
}

export interface SplitButtonProps<T extends string = string> {
  /** The selectable segments, rendered left-to-right. */
  options: SplitButtonOption<T>[];
  /** The currently-selected value (controlled). */
  value: T;
  /** Fired with the chosen value when a non-active segment is clicked. */
  onChange: (value: T) => void;
  size?: 'sm' | 'md';
  /** Stretch to fill the container, dividing width equally between segments. */
  fluid?: boolean;
  /** Disable the whole control. */
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
  'data-testid'?: string;
}

/**
 * A segmented control ("split button"): a row of mutually-exclusive segments
 * where the active one reads as pressed. Controlled via `value` / `onChange`.
 * Styling is token-driven (see SplitButton.css) so it matches the design system
 * in both themes.
 */
export function SplitButton<T extends string = string>({
  options,
  value,
  onChange,
  size = 'md',
  fluid = false,
  disabled = false,
  className = '',
  'aria-label': ariaLabel,
  'data-testid': dataTestId,
}: SplitButtonProps<T>) {
  const classes = [
    'fin-split-btn',
    `fin-split-btn--${size}`,
    fluid && 'fin-split-btn--fluid',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes} role="group" aria-label={ariaLabel} data-testid={dataTestId}>
      {options.map((option) => {
        const active = option.value === value;
        const optionClasses = ['fin-split-btn__option', active && 'fin-split-btn__option--active']
          .filter(Boolean)
          .join(' ');
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={active}
            disabled={disabled || option.disabled}
            data-testid={option['data-testid']}
            className={optionClasses}
            onClick={() => {
              if (!active) onChange(option.value);
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export default SplitButton;
